import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, Globe, ShoppingBag, Send, Store, AlertCircle, ExternalLink, RotateCcw, Loader2, Wand2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import VendorTemplatePicker from './VendorTemplatePicker';
import VendorScrapeImport from './VendorScrapeImport';
import VendorDescribeBusiness from './VendorDescribeBusiness';
import ClaimStorePicker from './ClaimStorePicker';

const VendorOnboarding = () => {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const prefillUrl = params.get('prefill_url') || '';
  const autoScan = params.get('autoscan') === '1' || params.get('autoscan') === 'true';
  // Default to "I have a website" because most real merchants do have one.
  // Anyone without can flip to describe-AI or templates with a single click.
  const initialTab = ['templates', 'describe', 'scrape', 'claim'].includes(params.get('tab'))
    ? params.get('tab')
    : 'scrape';
  const [tab, setTab] = useState(initialTab);
  const [existing, setExisting] = useState(null);   // {id, name, approval_status} or null
  const [restarting, setRestarting] = useState(false);

  // If the user is already linked to a vendor, load its name + status so we
  // can show the "continue editing / start over" banner instead of letting
  // them hit a confusing 409 on the scrape endpoint.
  useEffect(() => {
    if (!user?.vendor_id) { setExisting(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/vendor-admin/store');
        if (!cancelled) setExisting({
          id: data?.id || user.vendor_id,
          name: data?.name || 'your store',
          approval_status: data?.approval_status || 'approved',
        });
      } catch {
        if (!cancelled) setExisting({ id: user.vendor_id, name: 'your store', approval_status: 'pending' });
      }
    })();
    return () => { cancelled = true; };
  }, [user?.vendor_id]);

  const restart = async () => {
    if (!window.confirm("This will delete your pending store and all imported items. Continue?")) return;
    setRestarting(true);
    try {
      const { data } = await api.post('/onboarding/restart', {});
      toast.success(data?.message || 'Store cleared — you can start fresh now.');
      await refresh();           // re-fetch /auth/me so vendor_id clears
      setExisting(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not restart onboarding');
    } finally { setRestarting(false); }
  };

  const isPending = existing && existing.approval_status === 'pending';
  const isRejected = existing && existing.approval_status === 'rejected';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => nav('/')} className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 mb-4" data-testid="onboarding-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </button>

        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-600/30">
            <Send className="h-6 w-6 text-white -rotate-12" strokeWidth={2.3} />
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Become a ShipitNow vendor</h1>
            <p className="text-slate-500 mt-1">Pick the path that fits — AI does most of the heavy lifting.</p>
          </div>
        </div>

        <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50/60 p-3 text-[12px] text-emerald-900 leading-snug" data-testid="vendor-onboarding-mission">
          ShipItNow uses its <strong>registered and approved drivers</strong> to deliver food, same-day delivery, courier services, and business logistics in <strong>Canada and Ibadan</strong>. Sign up to sell to customers in Ottawa or Ibadan with zero delivery-ops headache.
        </div>

        {existing && (
          <div
            className={`mt-6 rounded-2xl border p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${
              isPending ? 'bg-amber-50 border-amber-200' : isRejected ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'
            }`}
            data-testid="existing-store-banner"
          >
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
              isPending ? 'bg-amber-500 text-white' : isRejected ? 'bg-rose-500 text-white' : 'bg-emerald-600 text-white'
            }`}>
              {isPending || isRejected ? <AlertCircle className="h-5 w-5" /> : <Store className="h-5 w-5" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate">
                You already manage <span className="text-slate-900">{existing.name}</span>
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                {isPending && 'Status: Awaiting super-admin approval. Continue polishing your menu while you wait — or start over with a different site.'}
                {isRejected && 'Status: Rejected by super-admin. You can start over with a new website or template.'}
                {!isPending && !isRejected && 'Status: Live on ShipitNow. To migrate to a different site, contact support.'}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0 flex-wrap">
              <Button onClick={() => nav('/vendor-admin/menu')} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="continue-editing-btn">
                <ExternalLink className="h-4 w-4 mr-1.5" /> Continue editing
              </Button>
              {(isPending || isRejected) && (
                <Button onClick={restart} disabled={restarting} variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="restart-onboarding-btn">
                  {restarting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}
                  Start over
                </Button>
              )}
            </div>
          </div>
        )}

        {!existing && (
          <Tabs value={tab} onValueChange={setTab} className="mt-7">
            <TabsList className="grid grid-cols-4 max-w-3xl bg-white border border-slate-200 p-1 h-auto" data-testid="onboarding-tabs">
              <TabsTrigger value="scrape" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 text-[12px] sm:text-sm" data-testid="tab-scrape">
                <Globe className="h-4 w-4 mr-1.5 shrink-0" /> <span className="hidden sm:inline">I have a</span> Website
              </TabsTrigger>
              <TabsTrigger value="describe" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 text-[12px] sm:text-sm" data-testid="tab-describe">
                <Wand2 className="h-4 w-4 mr-1.5 shrink-0" /> Describe<span className="hidden sm:inline"> business</span>
              </TabsTrigger>
              <TabsTrigger value="templates" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 text-[12px] sm:text-sm" data-testid="tab-templates">
                <Sparkles className="h-4 w-4 mr-1.5 shrink-0" /> Template<span className="hidden sm:inline">s</span>
              </TabsTrigger>
              <TabsTrigger value="claim" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white py-2 text-[12px] sm:text-sm" data-testid="tab-claim">
                <Store className="h-4 w-4 mr-1.5 shrink-0" /> Claim<span className="hidden sm:inline"> a store</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="scrape" className="mt-6">
              <VendorScrapeImport prefillUrl={prefillUrl} autoScan={autoScan} />
            </TabsContent>
            <TabsContent value="describe" className="mt-6">
              <VendorDescribeBusiness />
            </TabsContent>
            <TabsContent value="templates" className="mt-6">
              <VendorTemplatePicker />
            </TabsContent>
            <TabsContent value="claim" className="mt-6">
              <ClaimStorePicker />
            </TabsContent>
          </Tabs>
        )}

        <div className="mt-12 p-5 bg-white border border-slate-200 rounded-2xl flex items-start gap-4">
          <div className="h-10 w-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <ShoppingBag className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900">Customers can already shop anywhere</h3>
            <p className="text-sm text-slate-600 mt-1">If you'd rather keep selling on your own site, no setup needed. Your customers can paste your URL or upload a cart screenshot in our app and we'll dispatch a driver to pick up + deliver.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorOnboarding;
